/*!
 * o2pSchedule.js — per-stretch timing for o2p(), compiled one traversal at a time
 * © 2025 Rob Canning — GPLv3
 *
 * A TRAVERSAL is one pass of the path — what `dur:` times with a single
 * number. On a closed path that pass is a lap, but the path may equally be a
 * line or an arc, and nothing here assumes otherwise.
 *
 * One number per traversal is one speed per traversal. This cuts a traversal
 * into stretches so the speed can be written down as it changes: an object
 * setting off from rest, covering one part of the path faster than another,
 * each pass quicker than the last.
 *
 *   at:[0.25, 0.5, 0.75]   split points on the path, ABSOLUTE normalised
 *                          positions (0 and 1 implied) → 4 segments
 *   dur:[…]                SECONDS PER SEGMENT, indexed across the whole run
 *   ease:[…]               one curve per segment, same indexing
 *
 * Entries are picked by `(pass * nSeg + seg) % list.length`, so a 12-entry list
 * over 4 segments prescribes three distinct traversals and then repeats — the
 * same modulo cycling trans() uses for its per-leg lists.
 *
 * WHY A WHOLE TRAVERSAL IS COMPILED RATHER THAN SCHEDULED LEG BY LEG. The
 * obvious design makes each leg an iteration of the shared animation loop. It
 * also makes the loop's phase leg-local, and that phase is what animSync hands
 * between clients — two performers on different legs would silently desync, and
 * loop:N, freeze and nextOn:"cycle" would all start counting legs instead of
 * traversals. So instead the legs are folded into ONE duration and ONE
 * piecewise easing function: the loop keeps counting traversals, and the only thing that changes is the curve it is handed.
 *
 * Splits are absolute positions on the path, not offsets from where an object
 * happens to start, so several objects on one path all change speed at the same
 * places. An object starting mid-segment therefore covers a partial leg of that
 * segment at each end of its traversal; the two partials split that segment's
 * seconds between them, so **every object's traversal takes the prescribed time
 * whatever its start offset**. That invariant is what lets one profile be shared
 * by several of them, and it is pinned by a test.
 *
 * The wrap past the end of the path back to its beginning, when `start` is not
 * 0, is o2p's own reading of `start:` with no `end:` — this only
 * follows it. On an open path that wrap is a jump.
 *
 * Pure on purpose: no DOM, no anime, one import. o2p.js cannot be loaded under
 * node (its chain touches document.hidden), and this is the part worth testing.
 */

import { getEasing } from "../control/easing.js";

const EPS = 1e-9;

/** [0, …interior splits…, 1] — sorted, deduped, anything outside (0,1) dropped. */
function normalizeBounds(at) {
  const interior = (Array.isArray(at) ? at : [at])
    .map(Number)
    .filter(v => Number.isFinite(v) && v > EPS && v < 1 - EPS)
    .sort((a, b) => a - b)
    .filter((v, i, arr) => i === 0 || v - arr[i - 1] > EPS);
  return [0, ...interior, 1];
}

/** Which segment holds position u (u in [0,1)). */
function segmentAt(bounds, u) {
  for (let i = bounds.length - 2; i >= 0; i--) if (u >= bounds[i] - EPS) return i;
  return 0;
}

/**
 * Compile one traversal of the path into { dur, ease, legs }.
 *
 * `ease(p)` maps time through the traversal (0-1) to DISTANCE TRAVELLED from
 * the object's start (0-1) — the same thing o2p's easing returns,
 * which is why it drops straight into `globalT = (start + phase) % 1`.
 *
 * @param {number[]} at     interior split points, absolute path positions
 * @param {number[]} durs   seconds per segment
 * @param {*}        eases  easing name/node, or a list of them
 * @param {number}   start  where this object's traversal begins on the path, 0-1
 * @param {number}   pass   completed traversals, indexes into the lists
 */
export function compilePass({ at = [], durs = 1, eases = "linear", start = 0, pass = 0 } = {}) {
  const bounds = normalizeBounds(at);
  const nSeg = bounds.length - 1;
  const durList = (Array.isArray(durs) ? durs : [durs])
    .map(d => (Number(d) > 0 ? Number(d) : 1));
  const easeList = Array.isArray(eases) ? eases : [eases];
  const pick = (list, seg) => list[((pass * nSeg + seg) % list.length + list.length) % list.length];

  // With no splits the segment IS the whole traversal, so it must not be cut at
  // the object's start — that would apply the segment's easing twice per pass.
  const s0 = nSeg === 1 ? 0 : ((Number(start) || 0) % 1 + 1) % 1;

  const legs = [];
  let travelled = 0;
  let u = s0;
  for (let i = 0; i < nSeg + 1 && travelled < 1 - EPS; i++) {
    const seg = segmentAt(bounds, u);
    const segSpan = bounds[seg + 1] - bounds[seg];
    const span = Math.min(bounds[seg + 1] - u, 1 - travelled);
    if (span <= EPS) { u = (u + EPS) % 1; continue; }
    legs.push({
      seg,
      from: travelled,                                   // distance at leg start
      span,
      dur: pick(durList, seg) * (span / segSpan),        // a partial leg takes its share
      fn: getEasing(pick(easeList, seg)),
    });
    travelled += span;
    u = (u + span) % 1;
    if (u < EPS || u > 1 - EPS) u = 0;
  }

  const dur = legs.reduce((sum, l) => sum + l.dur, 0);

  // Cumulative time fraction at each leg's start — the prescriptive contract:
  // at t = these fractions the object is exactly on a split.
  let acc = 0;
  for (const leg of legs) {
    leg.t0 = acc / dur;
    acc += leg.dur;
    leg.t1 = acc / dur;
  }

  const ease = (p) => {
    if (!(p > 0)) return 0;
    if (p >= 1) return 1;
    let leg = legs[legs.length - 1];
    for (const l of legs) if (p < l.t1) { leg = l; break; }
    const width = leg.t1 - leg.t0;
    const local = width > EPS ? (p - leg.t0) / width : 1;
    return leg.from + leg.span * leg.fn(local);
  };

  return { dur, ease, legs };
}
